import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  Drawer,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { AdminBentoCategoryFull } from "schemas/admin";
import { SetAlertType } from "schemas/functions";

import { fetcherAuth } from "utils/api";

export type CategoryDrawerMode =
  | { kind: "edit"; category: AdminBentoCategoryFull }
  | { kind: "createTopLevel" };

type Props = {
  mode: CategoryDrawerMode | null;
  allCategories: AdminBentoCategoryFull[];
  onClose: () => void;
  onSaved: (saved: AdminBentoCategoryFull) => void;
  setAlert: SetAlertType;
};

type FormState = {
  name: string;
  gptDescription: string;
  gptExamples: string;
  standardizedTags: string;
  appListingCategories: string;
  autoArchive: boolean;
  doNotDisplay: boolean;
  useInOnboarding: boolean;
  isCreatorAgency: boolean;
  isLocalOnly: boolean;
  useInVaOnboarding: boolean;
  isLocationRelevant: boolean;
  vaDisplayName: string;
  oldCategoryName: string;
  categoryRelevancePrompt: string;
};

type SubcategoryEntry = { id: number; name: string };

const emptyForm: FormState = {
  name: "",
  gptDescription: "",
  gptExamples: "",
  standardizedTags: "",
  appListingCategories: "",
  autoArchive: false,
  doNotDisplay: false,
  useInOnboarding: false,
  isCreatorAgency: false,
  isLocalOnly: false,
  useInVaOnboarding: false,
  isLocationRelevant: true,
  vaDisplayName: "",
  oldCategoryName: "",
  categoryRelevancePrompt: "",
};

const toForm = (category: AdminBentoCategoryFull): FormState => ({
  name: category.name || "",
  gptDescription: category.gptDescription || "",
  gptExamples: category.gptExamples || "",
  standardizedTags: (category.standardizedTags || []).join(", "),
  appListingCategories: (category.appListingCategories || []).join(", "),
  autoArchive: category.autoArchive,
  doNotDisplay: category.doNotDisplay,
  useInOnboarding: category.useInOnboarding,
  isCreatorAgency: category.isCreatorAgency,
  isLocalOnly: category.isLocalOnly,
  useInVaOnboarding: category.useInVaOnboarding,
  isLocationRelevant: category.isLocationRelevant,
  vaDisplayName: category.vaDisplayName || "",
  oldCategoryName: category.oldCategoryName || "",
  categoryRelevancePrompt: category.categoryRelevancePrompt || "",
});

const splitCommaList = (value: string): string[] =>
  value
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean);

const formsEqual = (a: FormState, b: FormState): boolean =>
  Object.keys(a).every(
    (key) => a[key as keyof FormState] === b[key as keyof FormState],
  );

const subsEqual = (a: SubcategoryEntry[], b: SubcategoryEntry[]): boolean => {
  if (a.length !== b.length) return false;
  const aIds = a.map((s) => s.id).sort();
  const bIds = b.map((s) => s.id).sort();
  return aIds.every((id, i) => id === bIds[i]);
};

const EditCategoryDrawer = ({
  mode,
  allCategories,
  onClose,
  onSaved,
  setAlert,
}: Props) => {
  const { getToken } = useAuth();

  const [form, setForm] = useState<FormState>(emptyForm);
  const [initialForm, setInitialForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState<boolean>(false);
  const [selectedSubToAdd, setSelectedSubToAdd] =
    useState<AdminBentoCategoryFull | null>(null);

  const [localSubs, setLocalSubs] = useState<SubcategoryEntry[]>([]);
  const [initialSubs, setInitialSubs] = useState<SubcategoryEntry[]>([]);

  const modeKey = mode?.kind === "edit" ? mode.category.id : mode?.kind ?? null;

  useEffect(() => {
    if (mode?.kind === "edit") {
      const f = toForm(mode.category);
      setForm(f);
      setInitialForm(f);
      const subs = mode.category.subcategories || [];
      setLocalSubs(subs);
      setInitialSubs(subs);
    } else {
      setForm(emptyForm);
      setInitialForm(emptyForm);
      setLocalSubs([]);
      setInitialSubs([]);
    }
    setSelectedSubToAdd(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modeKey]);

  const subjectCategory = mode?.kind === "edit" ? mode.category : null;
  const isTopLevelEdit =
    subjectCategory !== null &&
    subjectCategory.parentCategoryNames.length === 0;

  const localSubIds = new Set(localSubs.map((s) => s.id));

  const availableSubcategories =
    isTopLevelEdit && subjectCategory
      ? allCategories.filter(
          (c) =>
            c.id !== subjectCategory.id &&
            c.subcategories.length === 0 &&
            !localSubIds.has(c.id),
        )
      : [];

  const isDirty =
    !formsEqual(form, initialForm) || !subsEqual(localSubs, initialSubs);

  if (!mode) return null;

  const isCreateTopLevel = mode.kind === "createTopLevel";

  const handleChange =
    <K extends keyof FormState>(key: K) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value =
        event.target.type === "checkbox"
          ? event.target.checked
          : event.target.value;
      setForm((prev) => ({ ...prev, [key]: value }) as FormState);
    };

  const buildBody = () => ({
    name: form.name,
    gptDescription: form.gptDescription,
    gptExamples: form.gptExamples || null,
    standardizedTags: splitCommaList(form.standardizedTags),
    appListingCategories: splitCommaList(form.appListingCategories),
    autoArchive: form.autoArchive,
    doNotDisplay: form.doNotDisplay,
    useInOnboarding: form.useInOnboarding,
    isCreatorAgency: form.isCreatorAgency,
    isLocalOnly: form.isLocalOnly,
    useInVaOnboarding: form.useInVaOnboarding,
    isLocationRelevant: form.isLocationRelevant,
    vaDisplayName: form.vaDisplayName || null,
    oldCategoryName: form.oldCategoryName || null,
    categoryRelevancePrompt: form.categoryRelevancePrompt || null,
  });

  const handleAddSubcategory = () => {
    if (!selectedSubToAdd) return;
    setLocalSubs((prev) =>
      [...prev, { id: selectedSubToAdd.id, name: selectedSubToAdd.name }].sort(
        (a, b) => a.name.localeCompare(b.name),
      ),
    );
    setSelectedSubToAdd(null);
  };

  const handleRemoveSubcategory = (subcategoryId: number) => {
    setLocalSubs((prev) => prev.filter((s) => s.id !== subcategoryId));
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // Save category fields
      const url = isCreateTopLevel
        ? `/api/admin/bento-categories`
        : `/api/admin/bento-categories/${subjectCategory!.id}`;
      const method = isCreateTopLevel ? "POST" : "PATCH";
      const res = await fetcherAuth(getToken, url, method, {}, buildBody());

      // Apply subcategory changes (edit mode only)
      if (subjectCategory) {
        const initialIds = new Set(initialSubs.map((s) => s.id));
        const currentIds = new Set(localSubs.map((s) => s.id));

        const toAdd = localSubs.filter((s) => !initialIds.has(s.id));
        const toRemove = initialSubs.filter((s) => !currentIds.has(s.id));

        for (const sub of toAdd) {
          await fetcherAuth(
            getToken,
            `/api/admin/bento-categories/${subjectCategory.id}/subcategories/${sub.id}`,
            "POST",
          );
        }
        for (const sub of toRemove) {
          await fetcherAuth(
            getToken,
            `/api/admin/bento-categories/${subjectCategory.id}/subcategories/${sub.id}`,
            "DELETE",
          );
        }

        // Re-fetch to get the server's final state
        if (toAdd.length > 0 || toRemove.length > 0) {
          const refreshed = await fetcherAuth(
            getToken,
            `/api/admin/bento-categories/${subjectCategory.id}`,
            "GET",
          );
          onSaved(refreshed.bentoCategory);
          return;
        }
      }

      onSaved(res.bentoCategory);
    } catch (error: any) {
      setAlert(error?.message || "Failed to save category", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleCloseAttempt = () => {
    onClose();
  };

  const renderSwitch = (label: string, key: keyof FormState) => (
    <FormControlLabel
      control={
        <Switch
          checked={Boolean(form[key])}
          onChange={handleChange(key)}
          name={key}
        />
      }
      label={label}
    />
  );

  return (
    <>
      <Drawer
        anchor="right"
        open
        onClose={handleCloseAttempt}
        PaperProps={{ sx: { width: { xs: "100%", md: 560 } } }}
      >
        <Box sx={{ p: 3 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Typography variant="h5">
              <strong>
                {isCreateTopLevel ? "New Category" : "Edit Category"}
              </strong>
            </Typography>
            <IconButton onClick={handleCloseAttempt}>
              <Box component="i" className="fa-regular fa-xmark" />
            </IconButton>
          </Stack>

          {subjectCategory && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              ID: {subjectCategory.id}
              {subjectCategory.parentCategoryNames.length > 0 && (
                <>
                  {" "}
                  · Parents: {subjectCategory.parentCategoryNames.join(", ")}
                </>
              )}
            </Typography>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Name"
                fullWidth
                value={form.name}
                onChange={handleChange("name")}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="GPT Description"
                fullWidth
                multiline
                minRows={3}
                value={form.gptDescription}
                onChange={handleChange("gptDescription")}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="GPT Examples"
                fullWidth
                multiline
                minRows={2}
                value={form.gptExamples}
                onChange={handleChange("gptExamples")}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Standardized Tags (comma-separated)"
                fullWidth
                multiline
                minRows={2}
                value={form.standardizedTags}
                onChange={handleChange("standardizedTags")}
                helperText="Example: vegan, cruelty-free, hair"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="App Listing Categories (comma-separated enum names)"
                fullWidth
                value={form.appListingCategories}
                onChange={handleChange("appListingCategories")}
                helperText="Example: android_beauty, android_shopping"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Category Relevance Prompt"
                fullWidth
                multiline
                minRows={3}
                value={form.categoryRelevancePrompt}
                onChange={handleChange("categoryRelevancePrompt")}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="VA Display Name"
                fullWidth
                value={form.vaDisplayName}
                onChange={handleChange("vaDisplayName")}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Old Category Name"
                fullWidth
                value={form.oldCategoryName}
                onChange={handleChange("oldCategoryName")}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Flags
              </Typography>
              <Stack direction="row" flexWrap="wrap" useFlexGap gap={1}>
                {renderSwitch("Auto-archive", "autoArchive")}
                {renderSwitch("Do not display", "doNotDisplay")}
                {renderSwitch("Use in onboarding", "useInOnboarding")}
                {renderSwitch("Use in VA onboarding", "useInVaOnboarding")}
                {renderSwitch("Is creator agency", "isCreatorAgency")}
                {renderSwitch("Is local only", "isLocalOnly")}
                {renderSwitch("Is location relevant", "isLocationRelevant")}
              </Stack>
            </Grid>
          </Grid>

          {subjectCategory && isTopLevelEdit && (
            <>
              <Divider sx={{ my: 4 }} />
              <Typography variant="h6" sx={{ mb: 1 }}>
                Subcategories
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Changes to subcategories are saved when you click Save.
              </Typography>

              {localSubs.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  No subcategories yet.
                </Typography>
              ) : (
                <Stack spacing={1} sx={{ mb: 2 }}>
                  {localSubs.map((sub) => (
                    <Stack
                      key={sub.id}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{
                        px: 1.5,
                        py: 1,
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Typography variant="body2">
                        {sub.name}{" "}
                        <Box
                          component="span"
                          sx={{ color: "text.secondary", ml: 0.5 }}
                        >
                          (#{sub.id})
                        </Box>
                      </Typography>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleRemoveSubcategory(sub.id)}
                      >
                        Remove
                      </Button>
                    </Stack>
                  ))}
                </Stack>
              )}

              <Stack direction="row" spacing={1} alignItems="center">
                <Autocomplete
                  options={availableSubcategories}
                  getOptionLabel={(option) => option.name}
                  value={selectedSubToAdd}
                  onChange={(_, value) => setSelectedSubToAdd(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select category to add"
                      size="small"
                    />
                  )}
                  sx={{ flex: 1 }}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                />
                <Button
                  disabled={!selectedSubToAdd}
                  variant="outlined"
                  onClick={handleAddSubcategory}
                >
                  Add
                </Button>
              </Stack>
            </>
          )}

          <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
            <LoadingButton
              loading={saving}
              variant="contained"
              onClick={handleSave}
              disabled={!isDirty && !isCreateTopLevel}
            >
              Save
            </LoadingButton>
            <Button
              variant="outlined"
              onClick={handleCloseAttempt}
              disabled={saving}
            >
              Cancel
            </Button>
          </Stack>
        </Box>
      </Drawer>
    </>
  );
};

export default EditCategoryDrawer;
